import React from 'react';
import { Server, Users, Target, Award, Zap, Shield, Heart, TrendingUp, CheckCircle, Linkedin, Twitter, Github } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
const AboutPage = () => {
  const values = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Security First",
      description: "We prioritize the security and privacy of your data above all else."
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Customer Success",
      description: "Your success is our success. We're committed to helping you achieve your goals."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Innovation",
      description: "We continuously innovate to provide cutting-edge solutions for modern challenges."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Collaboration",
      description: "We work closely with our clients as partners, not just service providers."
    }
  ];

  const stats = [
    { number: "500+", label: "Deployments" },
    { number: "99.9%", label: "Uptime SLA" },
    { number: "50+", label: "Happy Clients" },
    { number: "24/7", label: "Support" }
  ];

  const team = [
    {
      name: "Ahmed Ismail",
      role: "Founder & CEO",
      image: "👨‍💼",
      bio: "10+ years in DevOps and cloud architecture",
      social: { linkedin: "https://www.linkedin.com/in/ahmed-ismail-a86264b2/", twitter: "#", github: "#" }
    },
    {
      name: "Mohamed Sahal",
      role: "CTO",
      image: "👨‍💻",
      bio: "Expert in microservices and container orchestration",
      social: { linkedin: "#", twitter: "#", github: "#" }
    },
  ];

  const milestones = [
    { year: "2023", title: "Founded", description: "KaydSystems was established with a mission to simplify applications deployment and microservices management" },
    { year: "2024", title: "First 100 Clients", description: "Reached our first major milestone serving startups" },
    { year: "2025", title: "Enterprise Ready", description: "Launched enterprise-grade security features" },
    { year: "2025", title: "Global Expansion", description: "Expanded operations to serve clients worldwide" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      < Navbar/>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-2 mb-6">
            <Users className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-blue-400">About KaydSystems</span>
          </div>
          
          <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Empowering Teams with
            <span className="block bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Modern DevOps
            </span>
          </h1>
          
          <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-12">
            We're on a mission to make enterprise-grade DevOps accessible to startups and small businesses, 
            enabling them to deploy, scale, and manage their applications with confidence.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
                <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-slate-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-2 mb-6">
                <Target className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-cyan-400">Our Mission</span>
              </div>
              <h2 className="text-4xl font-bold mb-6">Building the Future of Infrastructure</h2>
              <p className="text-lg text-slate-400 mb-6">
                At KaydSystems, we believe that every company, regardless of size, deserves access to 
                enterprise-grade infrastructure and DevOps capabilities. Our mission is to democratize 
                cloud deployment and make it accessible, affordable, and efficient.
              </p>
              <p className="text-lg text-slate-400 mb-8">
                We've built a platform that combines the power of modern cloud infrastructure with 
                the simplicity that small teams need. No more hiring expensive DevOps engineers or 
                spending months setting up infrastructure.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Simplified Deployment</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Expert Support</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Scalable Solutions</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-400/20 blur-3xl"></div>
              <div className="relative bg-slate-900/50 border border-slate-800 rounded-2xl p-8">
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Zap className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Fast & Reliable</h3>
                      <p className="text-slate-400">Deploy in minutes, not days. 99.9% uptime guaranteed.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Secure by Default</h3>
                      <p className="text-slate-400">Enterprise-grade security without the complexity.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Scale with Ease</h3>
                      <p className="text-slate-400">Grow from 10 to 10,000 users seamlessly.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Core Values</h2>
            <p className="text-xl text-slate-400">The principles that guide everything we do</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 hover:border-blue-500/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500/10 to-cyan-400/10 rounded-lg flex items-center justify-center mb-4 text-blue-400">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-slate-400">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Journey</h2>
            <p className="text-xl text-slate-400">Key milestones in our growth story</p>
          </div>

          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex gap-8 group">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 group-hover:scale-110 transition-transform">
                    {milestone.year}
                  </div>
                  {index < milestones.length - 1 && (
                    <div className="w-0.5 h-full bg-gradient-to-b from-blue-500 to-cyan-400 mt-2"></div>
                  )}
                </div>
                <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 flex-1 group-hover:border-blue-500/50 transition-all">
                  <h3 className="text-2xl font-bold mb-2">{milestone.title}</h3>
                  <p className="text-slate-400">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Meet Our Team</h2>
            <p className="text-xl text-slate-400">The people behind KaydSystems</p>
          </div>

          <div className="flex flex-wrap justify-center gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 hover:border-blue-500/50 transition-all group">
                <div className="text-6xl mb-4 text-center">{member.image}</div>
                <h3 className="text-xl font-semibold mb-1 text-center">{member.name}</h3>
                <p className="text-blue-400 text-sm mb-3 text-center">{member.role}</p>
                <p className="text-slate-400 text-sm mb-4 text-center">{member.bio}</p>
                <div className="flex justify-center space-x-3">
                  <a href={member.social.linkedin} className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-500 transition-colors">
                    <Linkedin className="w-4 h-4" />
                  </a>
                  <a href={member.social.twitter} className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-cyan-400 transition-colors">
                    <Twitter className="w-4 h-4" />
                  </a>
                  <a href={member.social.github} className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-purple-500 transition-colors">
                    <Github className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-blue-500/10 to-cyan-400/10 border border-slate-800 rounded-2xl p-12">
            <h2 className="text-4xl font-bold mb-4">Ready to Work Together?</h2>
            <p className="text-xl text-slate-400 mb-8">
              Let's discuss how we can help transform your infrastructure.
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-xl hover:shadow-blue-500/50 transition-all"
            >
              <span>Get in Touch</span>
              <Award className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center text-slate-400 text-sm">
          <p>&copy; 2026 KaydSystems. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;